import React from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  Menu,
  MenuItem,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Loading from '../../components/loading';
import ErrorMessage from '../../components/errorMessage';
import SuccessMessage from '../../components/successMessage';
import ExpensesModal from './expensesModal';
import FinancialResult from '../FinancialResult';
import { useExpensesScreen, expenseStatusConfig } from '../hooks/useExpensesScreen';

export default function ExpensesMobile() {
  const {
    expenses,
    totalAmount,
    totalAmountToBePaid,
    totalAmountPaid,
    isLoading,
    selectedDate,
    setSelectedDate,
    goToPreviousMonth,
    goToNextMonth,
    menuAnchorEl,
    menuSelectedExpense,
    openMenu,
    closeMenu,
    openExpensesModal,
    setOpenExpensesModal,
    selectedExpenseId,
    addExpense,
    editExpense,
    payExpense,
    openDeleteModal,
    clickDelete,
    closeDeleteModal,
    confirmDelete,
    saveSuccess,
    showSuccessMessage,
    setShowSuccessMessage,
    successMessage,
    showErrorMessage,
    setShowErrorMessage,
    errorMessage,
  } = useExpensesScreen();

  return (
    <Box>
      <Loading isToHidden={isLoading === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.secondary', mb: 1.5 }}>
        Saídas
      </Typography>

      <Box sx={{ mb: 1.5 }}>
        <FinancialResult selectedDate={selectedDate} compact />
      </Box>

      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1.5 }}>
        <IconButton onClick={goToPreviousMonth} aria-label="Mês anterior">
          <ChevronLeft />
        </IconButton>
        <DatePicker
          label="mês e ano"
          views={['month', 'year']}
          value={selectedDate}
          onChange={(date) => setSelectedDate(date)}
          slotProps={{ textField: { fullWidth: true } }}
        />
        <IconButton onClick={goToNextMonth} aria-label="Próximo mês">
          <ChevronRight />
        </IconButton>
      </Stack>

      <Button
        data-cy="btn-add-expense"
        fullWidth
        variant="contained"
        startIcon={<AddIcon />}
        onClick={addExpense}
        sx={{ mb: 2 }}
      >
        Adicionar Saída
      </Button>

      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        {expenses.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              Nenhuma saída encontrada para o período.
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {expenses.map((expense, index) => (
              <React.Fragment key={expense.id}>
                <ListItem
                  sx={{ display: 'block', py: 1.5, px: 2 }}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      aria-label="ações da saída"
                      onClick={(event) => openMenu(event, expense)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  }
                >
                  <Box sx={{ pr: 4 }}>
                    <Typography variant="body2" fontWeight="bold" sx={{ overflowWrap: 'anywhere' }}>
                      {expense.description}
                    </Typography>

                    {expense.observation && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', fontStyle: 'italic', mt: 0.25 }}
                      >
                        {expense.observation}
                      </Typography>
                    )}

                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ mt: 1 }}
                    >
                      <Chip
                        label={expense.status}
                        color={expenseStatusConfig[expense.status].color}
                        variant={expenseStatusConfig[expense.status].variant}
                        size="small"
                        sx={{ fontWeight: 'bold' }}
                      />
                      <Typography variant="body2" fontWeight="medium">
                        {expense.amount}
                      </Typography>
                    </Stack>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 0.5 }}
                    >
                      Vence em {expense.dueDate}
                      {expense.category ? ` · ${expense.category}` : ''}
                    </Typography>
                  </Box>
                </ListItem>
                {index < expenses.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Card>

      {expenses.length > 0 && (
        <Stack spacing={0.5} sx={{ mt: 2, px: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Total: <strong>{totalAmount}</strong>
          </Typography>
          <Typography variant="caption" color="success.main">
            Total Pago: <strong>{totalAmountPaid}</strong>
          </Typography>
          <Typography variant="caption" color="warning.main">
            Total a Pagar: <strong>{totalAmountToBePaid}</strong>
          </Typography>
        </Stack>
      )}

      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={closeMenu}>
        <MenuItem onClick={editExpense}>Editar</MenuItem>
        <MenuItem onClick={payExpense} disabled={menuSelectedExpense?.status === 'Pago'}>
          Pagar
        </MenuItem>
        <MenuItem onClick={clickDelete} sx={{ color: 'error.main' }}>
          Excluir
        </MenuItem>
      </Menu>

      {openExpensesModal && (
        <ExpensesModal
          open
          onClose={() => setOpenExpensesModal(false)}
          onSaveSuccess={saveSuccess}
          expenseId={selectedExpenseId}
        />
      )}

      <Dialog open={openDeleteModal} onClose={closeDeleteModal} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent dividers>
          <DialogContentText>
            Tem certeza que deseja excluir essa saída?
            <br />
            Essa ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteModal} color="primary" variant="outlined">
            Cancelar
          </Button>
          <Button onClick={confirmDelete} color="error" variant="outlined" autoFocus>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
